/**
 * POST /api/feedback — create a product-feedback item (any signed-in user).
 * See `backend/src/feedback/feedback-routes.ts` / `backend/src/feedback/types.ts`
 * for the authoritative contract this mirrors.
 *
 * Shared create/list/item schema surface consumed by Tendril-conversational
 * capture and the admin review page.
 */
import { z } from 'zod';

export const FeedbackTypeSchema = z.enum(['suggestion', 'feature_request', 'bug_report']);
export type FeedbackType = z.infer<typeof FeedbackTypeSchema>;

export const FeedbackStatusSchema = z.enum(['new', 'reviewed', 'resolved']);
export type FeedbackStatus = z.infer<typeof FeedbackStatusSchema>;

export const FeedbackPageContextSchema = z.looseObject({
  url: z.string(),
  section: z.string().optional(),
});
export type FeedbackPageContext = z.infer<typeof FeedbackPageContextSchema>;

export const FeedbackCreateResponseSchema = z.looseObject({
  feedbackId: z.string().min(1),
  type: FeedbackTypeSchema,
  text: z.string(),
  priority: z.string().optional(),
  screenshotDescription: z.string().optional(),
  details: z.string().optional(),
  pageContext: FeedbackPageContextSchema,
  userId: z.string(),
  userEmail: z.string(),
  createdAt: z.string(),
  status: FeedbackStatusSchema,
});
export type FeedbackCreateResponse = z.infer<typeof FeedbackCreateResponseSchema>;

// ---------------------------------------------------------------------------
// Phase 3 (admin list/status) — extends the base shape above rather than
// duplicating it. Mirrors `backend/src/feedback/types.ts` `Feedback` /
// `FeedbackOrigin`.
// ---------------------------------------------------------------------------

export const FeedbackOriginSchema = z.enum(['direct', 'interview']);
export type FeedbackOrigin = z.infer<typeof FeedbackOriginSchema>;

/** A full `Feedback` item as returned by GET /api/feedback (admin list). */
export const FeedbackItemSchema = FeedbackCreateResponseSchema.extend({
  origin: FeedbackOriginSchema,
  classifierVerdict: z.string().optional(),
  attemptedUtterance: z.string().optional(),
});
export type FeedbackItem = z.infer<typeof FeedbackItemSchema>;

/**
 * Per-row-tolerant items array. A single row that fails validation must NOT
 * blank the entire admin review surface: with a plain `z.array(FeedbackItemSchema)`
 * the parse is all-or-nothing, so one legacy row (missing the redesign's required
 * `origin`, or carrying the retired `tendril_interview` value) hides every valid
 * row (see UAT-REQ-1, run 20260709-userfeedback-02). We `safeParse` each element
 * and keep only the valid ones; invalid/legacy rows are dropped rather than fatal.
 * Production has no legacy feedback data, so this only ever drops rows in dev or
 * after a partial backfill — a defensive floor for an admin triage view.
 */
const tolerantFeedbackItems = z
  .array(z.unknown())
  .transform((rows) =>
    rows.flatMap((row) => {
      const parsed = FeedbackItemSchema.safeParse(row);
      return parsed.success ? [parsed.data] : [];
    }),
  );

export const FeedbackListResponseSchema = z.looseObject({
  items: tolerantFeedbackItems,
  nextCursor: z.string().nullable().optional(),
  scannedCount: z.number().optional(),
  truncated: z.boolean().optional(),
});
export type FeedbackListResponse = z.infer<typeof FeedbackListResponseSchema>;

/** PUT /api/feedback/{id}/status response — the updated Feedback item. */
export const FeedbackStatusUpdateResponseSchema = FeedbackItemSchema;
export type FeedbackStatusUpdateResponse = FeedbackItem;
